// src/hooks/queries/useReportes.ts
/**
 * Hooks de TanStack Query para Reportes, Dashboards y Metricas.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportesService } from '@/services/reportes';
import { PeriodoReporte } from '@/types/reporte';
import type {
  CategoriaMetrica,
  TipoReporte,
  FiltrosReporte,
  DashboardCreate,
  DashboardUpdate,
  DashboardWidgetCreate,
  DashboardWidgetUpdate,
  ReporteRequest,
  ReporteResponse,
  GuardarReporteRequest,
  ExportarReporteRequest,
} from '@/types/reporte';

// =============================================================================
// METRICAS
// =============================================================================

/**
 * Lista todas las métricas disponibles.
 * @param categoria - Opcional: filtrar por categoria
 */
export function useMetricas(categoria?: CategoriaMetrica) {
  return useQuery({
    queryKey: ['metricas', { categoria }],
    queryFn: () => reportesService.listMetricas(categoria),
  });
}

/**
 * Obtiene el valor de una métrica específica.
 * @param codigo - Código de la métrica
 * @param periodo - Periodo para calcular
 * @param filtros - Filtros adicionales
 */
export function useMetricaValor(
  codigo: string,
  periodo?: PeriodoReporte,
  filtros?: { fecha_inicio?: string; fecha_fin?: string; cliente_id?: string }
) {
  return useQuery({
    queryKey: ['metricas', codigo, { periodo, ...filtros }],
    queryFn: () =>
      reportesService.getMetricaValor(codigo, {
        periodo,
        ...filtros,
      }),
    enabled: !!codigo,
  });
}

/**
 * Mutation para calcular múltiples métricas.
 */
export function useCalcularMetricas() {
  return useMutation({
    mutationFn: (data: {
      codigos: string[];
      periodo?: PeriodoReporte;
      filtros?: FiltrosReporte;
    }) => reportesService.calcularMetricas(data),
  });
}

// =============================================================================
// DASHBOARDS
// =============================================================================

/**
 * Lista los dashboards del usuario.
 * @param incluirCompartidos - Incluir dashboards compartidos
 */
export function useDashboards(incluirCompartidos: boolean = true) {
  return useQuery({
    queryKey: ['dashboards', { incluirCompartidos }],
    queryFn: () => reportesService.listDashboards(incluirCompartidos),
  });
}

/**
 * Obtiene un dashboard específico con sus datos calculados.
 * @param id - ID del dashboard
 * @param periodo - Periodo para calcular los datos
 */
export function useDashboard(id: string, periodo?: PeriodoReporte) {
  return useQuery({
    queryKey: ['dashboards', id, { periodo }],
    queryFn: () => reportesService.getDashboard(id, periodo),
    enabled: !!id,
  });
}

/**
 * Obtiene el dashboard por defecto del usuario.
 * @param periodo - Periodo para calcular los datos
 */
export function useDashboardDefault(periodo?: PeriodoReporte) {
  return useQuery({
    queryKey: ['dashboards', 'default', { periodo }],
    queryFn: () => reportesService.getDefaultDashboard(periodo),
  });
}

/**
 * Mutation para crear un nuevo dashboard.
 */
export function useCreateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DashboardCreate) => reportesService.createDashboard(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}

/**
 * Mutation para actualizar un dashboard.
 */
export function useUpdateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DashboardUpdate }) =>
      reportesService.updateDashboard(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['dashboards', id] });
    },
  });
}

/**
 * Mutation para eliminar un dashboard.
 */
export function useDeleteDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reportesService.deleteDashboard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}

/**
 * Mutation para establecer un dashboard como default.
 */
export function useSetDashboardDefault() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reportesService.setDefaultDashboard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}

/**
 * Mutation para duplicar un dashboard.
 */
export function useDuplicarDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, nombre }: { id: string; nombre: string }) =>
      reportesService.duplicarDashboard(id, nombre),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}

/**
 * Mutation para agregar un widget a un dashboard.
 */
export function useAddWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      data,
    }: {
      dashboardId: string;
      data: DashboardWidgetCreate;
    }) => reportesService.addWidget(dashboardId, data),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards', dashboardId] });
    },
  });
}

/**
 * Mutation para actualizar un widget.
 */
export function useUpdateWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      widgetId,
      data,
    }: {
      dashboardId: string;
      widgetId: string;
      data: DashboardWidgetUpdate;
    }) => reportesService.updateWidget(dashboardId, widgetId, data),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards', dashboardId] });
    },
  });
}

/**
 * Mutation para eliminar un widget.
 */
export function useDeleteWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      widgetId,
    }: {
      dashboardId: string;
      widgetId: string;
    }) => reportesService.deleteWidget(dashboardId, widgetId),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards', dashboardId] });
    },
  });
}

// =============================================================================
// REPORTES
// =============================================================================

/**
 * Mutation para generar un reporte.
 */
export function useGenerarReporte() {
  return useMutation({
    mutationFn: (data: ReporteRequest) => reportesService.generarReporte(data),
  });
}

/**
 * Obtiene el reporte operativo para un periodo de fechas.
 * @param fechaInicio - Fecha de inicio
 * @param fechaFin - Fecha de fin
 * @param clienteId - Opcional: filtrar por cliente
 */
export function useReporteOperativo(
  fechaInicio: string,
  fechaFin: string,
  clienteId?: string
) {
  return useQuery({
    queryKey: ['reportes', 'operativo', { fechaInicio, fechaFin, clienteId }],
    queryFn: () => reportesService.getReporteOperativo(fechaInicio, fechaFin, clienteId),
    enabled: !!fechaInicio && !!fechaFin,
  });
}

/**
 * Obtiene el reporte gerencial para un periodo.
 * @param periodo - Periodo del reporte
 */
export function useReporteGerencial(periodo: PeriodoReporte = PeriodoReporte.MES) {
  return useQuery({
    queryKey: ['reportes', 'gerencial', { periodo }],
    queryFn: () => reportesService.getReporteGerencial(periodo),
  });
}

/**
 * Obtiene el reporte de un cliente específico.
 * @param clienteId - ID del cliente
 * @param periodo - Periodo del reporte
 */
export function useReporteCliente(clienteId: string, periodo: PeriodoReporte = PeriodoReporte.MES) {
  return useQuery({
    queryKey: ['reportes', 'cliente', clienteId, { periodo }],
    queryFn: () => reportesService.getReporteCliente(clienteId, periodo),
    enabled: !!clienteId,
  });
}

/**
 * Lista los reportes guardados del usuario.
 * @param tipo - Opcional: filtrar por tipo
 */
export function useReportesGuardados(tipo?: TipoReporte) {
  return useQuery({
    queryKey: ['reportes', 'guardados', { tipo }],
    queryFn: () => reportesService.listReportesGuardados(tipo),
  });
}

/**
 * Mutation para guardar un reporte.
 */
export function useGuardarReporte() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      datos,
      reporteData,
    }: {
      datos: GuardarReporteRequest;
      reporteData: ReporteResponse;
    }) => reportesService.guardarReporte(datos, reporteData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes', 'guardados'] });
    },
  });
}

/**
 * Mutation para eliminar un reporte guardado.
 */
export function useEliminarReporteGuardado() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reportesService.deleteReporteGuardado(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes', 'guardados'] });
    },
  });
}

/**
 * Mutation para exportar un reporte a PDF/Excel/CSV.
 */
export function useExportarReporte() {
  return useMutation({
    mutationFn: ({
      reporteId,
      data,
    }: {
      reporteId: string;
      data: ExportarReporteRequest;
    }) => reportesService.exportarReporte(reporteId, data),
  });
}
